/**
 * (c) 2009-2024 Clinithink confidential.
 * Please see LICENSE at the project root for details.
 *
 * @file
 * Authentication React Context
 *
 * Provides interaction with the Authentication API
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMsal } from "@azure/msal-react";
import PropTypes from "prop-types";
import Api from "../api";
import MockApi from "../mock-api";

const UserContext = createContext();
const useUser = () => useContext(UserContext);

const UserProvider = ({ children, backend }) => {
  const { instance, accounts } = useMsal();
  const [userRoles, setUserRoles] = useState([]);
  const [isCSRFLoaded, setIsCSRFLoaded] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await backend.callUserDetails();
      if (!res?.data?.roles) {
        throw new Error("Roles data not available in response");
      }
      setUserRoles(res.data.roles);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  }, [backend]);

  useEffect(() => {
    if (isCSRFLoaded) {
      fetchData();
    }
  }, [instance, accounts, isCSRFLoaded, fetchData]);

  const loadCSRFToken = useCallback(() => {
    backend
      .getCSRFToken()
      .then((res) => {
        localStorage.setItem("csrf", res.message);
        setIsCSRFLoaded(true);
      })
      .catch((err) => {
        console.warn("Failed to load CSRF token", err);
        setIsCSRFLoaded(false);
      });
  }, [backend]);

  useEffect(() => {
    loadCSRFToken();
  }, [loadCSRFToken]);

  const providerValues = useMemo(
    () => ({
      userRoles,
      backend,
    }),
    [backend, userRoles]
  );

  return <UserContext.Provider value={providerValues}>{children}</UserContext.Provider>;
};

UserProvider.defaultProps = {
  children: null,
};

UserProvider.propTypes = {
  children: PropTypes.element,
  backend: PropTypes.oneOfType([PropTypes.instanceOf(Api), PropTypes.instanceOf(MockApi)])
    .isRequired,
};

export { UserProvider, useUser };
